import { FC, useEffect, useRef, useState } from "react"
import { RecordKey, RecordValueWithSensograms, getFullRecords } from "../idb/idb"
import Plot from "react-plotly.js"
import { FlexRow, FlexCol, Paper } from "../common"
import { aggregateSignature, computeSignature, normalizeL2, sortSignature } from "../analysis/compute"
import { getPcaFigure, getSignaturesFigure } from "../analysis/figures"
import { getColormap } from "../analysis/colormap"
import { performAnalysis } from "../analysis/pca"
import { DEFAULT_PLOTLY_MARGIN } from "../constants"
import { Button, Col, InputNumber, Row, Tooltip, Typography, message as antdMessage } from "antd"
import { JsonView, defaultStyles } from "react-json-view-lite"
import { ModelType, decodeModelBase64, encodeModelBase64 } from "../byteio/model"
import { saveModel, deleteModel, loadModelSigmaX, loadModelSigmaY, saveModelSigmaX, saveModelSigmaY, loadModel } from "../localStorage"
import { optimizeEllipsesSigma, scaleEllipsesSigma } from "../analysis/ellipse"
import { CopyOutlined, DeleteOutlined, DownloadOutlined, InfoCircleTwoTone, LoginOutlined, RedoOutlined, RocketOutlined, SaveOutlined } from "@ant-design/icons"
import { ColormapWidget } from "./colormapWidget"
import { useMediaQuery } from "../utils"
import { ModeledPca } from "./modeledPca"
import Input, { InputRef } from "antd/es/input/Input"


export const ExternalModelWidget: FC = () => {
    const [ currentModel, setCurrentModel ] = useState<ModelType | null>(null)
    const [ inputStatus, setInputStatus ] = useState<'error' | undefined>(undefined)
    const inputRef = useRef<InputRef>(null)

    return <Paper>
        <Row gutter={[10, 10]} justify="center">
        <Col xs={24}>
            <Typography.Title level={4}>Use External Model</Typography.Title>
        </Col>
        <Col xs={24}>
            <Input
                showCount
                placeholder="Paste the encoded model here.."
                ref={inputRef}
                status={inputStatus}
                suffix={<Button icon={<LoginOutlined />} onClick={() => {
                    if (inputRef.current === null || inputRef.current.input === null) {
                        return
                    }
                    try {
                        let value = inputRef.current.input.value
                        let decodedModel = decodeModelBase64(value)
                        setCurrentModel(decodedModel)
                        antdMessage.success('Model loaded!')
                    } catch(e: any) {
                        console.error(e)
                        antdMessage.error(`Error while loading model: ${e.message}`)
                        setInputStatus('error')
                    }
                }}/>}
            />
        </Col>
        <Col xs={24} lg={12} style={{
            height: 250
        }}>
            <ModeledPca model={currentModel} result={null} />
        </Col>
    </Row>
    <Row justify="end" align="middle" gutter={[10, 10]}>
        <Col flex="auto">
            <InfoCircleTwoTone/>
            <span> You can switch your own model building by starting <b>analyzing</b> own records</span>
        </Col>
        <Col>
            <Button block type="primary" disabled={currentModel === null} onClick={() => {
                if (currentModel === null) {
                    return
                }
                saveModel(
                    currentModel
                )
                antdMessage.success('Model engaged!')
            }} icon={<RocketOutlined />}>
                Engage Model
            </Button>
        </Col>
        <Col>
            <Tooltip overlay="Copy to use as an external model elsewhere!">
                <Button
                    onClick={() => {
                        if (inputRef.current === null || inputRef.current.input === null) {
                            return
                        }
                        navigator.clipboard.writeText(inputRef.current.input.value)
                        antdMessage.success('Model copied to clipboard!')
                    }}
                    icon={<CopyOutlined/>}
                />
            </Tooltip>
        </Col>
        <Col>
            <Tooltip overlay="Discard the model">
                <Button danger
                    onClick={() => {
                        deleteModel()
                        antdMessage.warning('Model discarded!')
                    }}
                    icon={<DeleteOutlined/>}
                />
            </Tooltip>
        </Col>
    </Row>
    </Paper>
}

export const RecordsAnalysisWidget: FC<{
    recordKeys: RecordKey[]
    recordNames: string[] // used to trigger re-render
}> = ({
    recordKeys,
    recordNames
}) => {
    
    const [ records, setRecords ] = useState<RecordValueWithSensograms[]>([])
    const [ signatures, setSignatures ] = useState<number[][]>([])
    const [ commonSpotsgrid, setCommonSpotsgrid ] = useState<number[]>([])

    const [ signaturesPlotlyData, setSignaturesPlotlyData ] = useState<Plotly.Data[]>([])
    const [ signaturesPlotlyLayout, setSignaturesPlotlyLayout ] = useState<Partial<Plotly.Layout>>({})

    const [ pcaPlotlyData, setPcaPlotlyData ] = useState<Plotly.Data[]>([])
    const [ pcaPlotlyLayout, setPcaPlotlyLayout ] = useState<Partial<Plotly.Layout>>({})

    const [ currentModel, setCurrentModel ] = useState<ModelType | null>(null)
    const [ b64encodedModel, setB64encodedModel ] = useState<string>('')

    const [ sigmaMultiplierRx, setSigmaMultiplierRx ] = useState<number | null>(loadModelSigmaX())
    const [ sigmaMultiplierRy, setSigmaMultiplierRy ] = useState<number | null>(loadModelSigmaY())

    const [ analysis, setAnalysis ] = useState<{
        pcaExplainedVarianceRatio: number[]
        groupedProjections: Record<string, number[][]>
        groupedEllipses: Record<string, number[]>
        groupedScaledEllipses: Record<string, number[]>
        pcaEigenvectors: number[][]
    } | null>(null)
    const [ cmap, setCmap ] = useState<Record<string, [number, number, number]>>({})

    const isMobile = useMediaQuery()

    useEffect(() => {
        getFullRecords(recordKeys).then((_records) => {
            _records.sort((a, b) => {
                return b.absoluteTimestamp - a.absoluteTimestamp
            })
            setRecords(_records)
        })
    }, [recordKeys, recordNames])

    useEffect(() => {
        const f = () => {
            if (records.length === 0) {
                return
            }
            let signatures: number[][] = []
            let spotsgrids1d: number[][] = []
            for (let record of records) {
                let _signature = computeSignature(record)
                let _spotsgrid1d = record.device.spotsgrid
    
                let excludedSignature: number[] = []
                let excludedSpotsgrid1d: number[] = []
                for (let i = 0; i < _signature.length; i++) {
                    let sensorInt = _spotsgrid1d[i]
                    if (sensorInt >= 1) {
                        excludedSignature.push(_signature[i])
                        excludedSpotsgrid1d.push(sensorInt)
                    }
                }
    
                // always aggregate
    
                let [ aggregatedExcludedSignature, aggregatedSpotsgrid1d ] = aggregateSignature(
                    excludedSignature,
                    excludedSpotsgrid1d
                )
                let normalizedSignature = normalizeL2(aggregatedExcludedSignature)
                let [ sortedNormalizedSignature, sortedAggregatedSpotsgrid1d ] = sortSignature(
                    aggregatedSpotsgrid1d,
                    normalizedSignature,
                )
                spotsgrids1d.push(sortedAggregatedSpotsgrid1d)
                signatures.push(sortedNormalizedSignature)
            }
            const signatureLength = signatures[0].length
            for (let sign of signatures) {
                if (sign.length !== signatureLength) {
                    throw new Error('signatures have different lengths')
                }
            }
            // check if all spotsgrids are the same
            const commonSpotsgrid = spotsgrids1d[0]
            for (let spotsgrid1d of spotsgrids1d) {
                if (spotsgrid1d.length !== commonSpotsgrid.length) {
                    throw new Error('spotsgrids have different lengths')
                }
                for (let i = 0; i < spotsgrid1d.length; i++) {
                    if (spotsgrid1d[i] !== commonSpotsgrid[i]) {
                        throw new Error('spotsgrids are not the same')
                    }
                }
            }
            setCommonSpotsgrid(commonSpotsgrid)
            setSignatures(signatures)
        }

        try {
            f()
        } catch (e: any) {
            console.error(e)
            antdMessage.error(`Error while computing signatures: ${e.message}`)
        }
    }, [records])

    useEffect(() => {

        if (signatures.length === 0) {
            return
        }

        let labels = records.map((record) => record.name ? record.name : 'unnamed')
        let uniqueLabels = Array.from(new Set(labels))
        let cmap = getColormap(uniqueLabels)
        console.log('analysis widget: cmap', cmap)

        let analysis = performAnalysis(signatures, labels)

        setAnalysis(analysis)
        setCmap(cmap)

    }, [signatures])

    useEffect(() => {
        if (signatures.length ===  0) {
            return 
        }
        if (analysis === null) {
            return
        }

        // console.log("analysis widget: sigmaMultiplierRx", sigmaMultiplierRx, "sigmaMultiplierRy", sigmaMultiplierRy)
        // console.log("analysis widget: grouped ellipses", analysis.groupedEllipses)
        if (sigmaMultiplierRx === null || sigmaMultiplierRy === null) {
            let [ _, _sigmaRx, _sigmaRy ] = optimizeEllipsesSigma(
                signatures,
                analysis.groupedScaledEllipses,
            )
            if (sigmaMultiplierRx === null) {
                setSigmaMultiplierRx(_sigmaRx)
                // saveModelSigmaX(_sigmaRx)
            }
            if (sigmaMultiplierRy === null) {
                setSigmaMultiplierRy(_sigmaRy)
                // saveModelSigmaY(_sigmaRy)
            }
        }

        let labels = records.map((record) => record.name ? record.name : 'unnamed')
        let uniqueLabels = Array.from(new Set(labels))
        let cmap = getColormap(uniqueLabels)

        if (sigmaMultiplierRx !== null && sigmaMultiplierRy !== null) {
            analysis.groupedScaledEllipses = scaleEllipsesSigma(
                analysis.groupedEllipses,
                sigmaMultiplierRx,
                sigmaMultiplierRy
            )
        }

        let {
            data: signaturesPlotlyData,
            layout: signaturesPlotlyLayout,
        } = getSignaturesFigure(
            commonSpotsgrid,
            signatures,
            labels,
            cmap,
        )

        
        let {
            data: pcaPlotlyData,
            layout: pcaPlotlyLayout,
        } = getPcaFigure(
            cmap,
            analysis.pcaExplainedVarianceRatio,
            analysis.groupedProjections,
            analysis.groupedScaledEllipses,
        )

        signaturesPlotlyLayout.margin = DEFAULT_PLOTLY_MARGIN
        pcaPlotlyLayout.margin = DEFAULT_PLOTLY_MARGIN

        signaturesPlotlyLayout.showlegend = false
        pcaPlotlyLayout.showlegend = false

        setSignaturesPlotlyData(signaturesPlotlyData)
        setSignaturesPlotlyLayout(signaturesPlotlyLayout)

        setPcaPlotlyData(pcaPlotlyData)
        setPcaPlotlyLayout(pcaPlotlyLayout)

        let _currentModel = {
            groupedScaledEllipses: analysis.groupedScaledEllipses,
            pcaEigenvectors: analysis.pcaEigenvectors,
            commonSpotsgrid,
            cmap
        }
        // console.log("analysis widget: setting current model", _currentModel)
        // Actual model
        setCurrentModel(_currentModel)
        // Encoded model
        let _b64encodedModel = encodeModelBase64(_currentModel)
        setB64encodedModel(_b64encodedModel)
    }, [analysis, sigmaMultiplierRx, sigmaMultiplierRy])

    if (records.length === 0) {
        return null
    }

    return <FlexCol>
        <Paper>
        <FlexCol>
            <Typography.Title level={4}>Analyze & Build Model</Typography.Title>
            <Row gutter={[10, 10]} align={'stretch'}>
                <Col xs={24} lg={24}>
                    <ColormapWidget cmap={cmap} />
                </Col>
                <Col xs={24} lg={12} style={{
                    height: 400,
                }}>
                    <Plot
                        data={signaturesPlotlyData}
                        layout={signaturesPlotlyLayout}
                        config={{
                            staticPlot: isMobile
                        }}
                        useResizeHandler
                        style={{
                            width: '100%',
                            height: '100%',
                            margin: 'auto',                    
                        }}
                    />
                </Col>
                <Col xs={24} lg={12} style={{
                    height: 400,
                }}>
                    <Plot
                        data={pcaPlotlyData}
                        layout={pcaPlotlyLayout}
                        config={{
                            staticPlot: isMobile
                        }}
                        useResizeHandler
                        style={{
                            width: '100%',
                            height: '100%',
                            margin: 'auto',
                        }}
                    />
                </Col>
                <Col xs={24} lg={24}>
                    <ColormapWidget cmap={cmap} />
                </Col>
            </Row>
            <Row justify="center" align="bottom" gutter={[10, 10]} style={{
                width: '100%',
            }}>
                <Col>
                    <FlexCol style={{
                        gap: 3
                    }}>
                        Tolerance X: 
                        <InputNumber
                            min={1}
                            value={sigmaMultiplierRx}
                            onChange={(value) => {
                                if (value === null) {
                                    return
                                }
                                setSigmaMultiplierRx(value as number)
                                saveModelSigmaX(value as number)
                            }
                        }/>
                    </FlexCol>
                </Col>
                <Col>
                    <FlexCol style={{
                        gap: 3
                    }}>
                        Tolerance Y:
                        <InputNumber
                            min={1}
                            value={sigmaMultiplierRy}
                            onChange={(value) => {
                                if (value === null) {
                                    return
                                }
                                setSigmaMultiplierRy(value as number)
                                saveModelSigmaY(value as number)
                            }
                        }/>
                    </FlexCol>
                </Col>
                <Col>
                    <Tooltip overlay={<>Revert to auto-generated ellipses</>}>
                        <Button
                            icon={
                                <RedoOutlined />
                            }
                            onClick={() => {
                                setSigmaMultiplierRx(null)
                                setSigmaMultiplierRy(null)
                            }}
                        />
                    </Tooltip>
                </Col>
            </Row>
            { currentModel !== null &&
                <Row justify="end" align="middle" gutter={[10, 10]}>
                    <Col flex="auto">
                        <InfoCircleTwoTone/>
                        <span> You can switch to external model by <b>clearing</b> the analysis</span>
                    </Col>
                    <Col>
                        <Button block type="primary" onClick={() => {
                            try {
                                saveModel(
                                    currentModel
                                )
                                antdMessage.success('Model engaged!')
                            } catch (e: any) {
                                console.error(e)
                                antdMessage.error(`Error while encoding model: ${e.message}`)
                            }
                        }} icon={<RocketOutlined />}>
                            Engage Model
                        </Button>
                    </Col>
                    <Col>
                        <Tooltip overlay="Copy to use as an external model elsewhere!">
                            <Button
                                onClick={() => {
                                    navigator.clipboard.writeText(b64encodedModel)
                                    antdMessage.success('Model copied to clipboard!')
                                }}
                                icon={<CopyOutlined/>}
                            />
                        </Tooltip>
                    </Col>
                    <Col>
                        <Tooltip overlay="Discard the model">
                            <Button danger
                                onClick={() => {
                                    deleteModel()
                                    antdMessage.warning('Model discarded!')
                                }}
                                icon={<DeleteOutlined/>}
                            />
                        </Tooltip>
                    </Col>
                </Row>
            }
        </FlexCol>
    </Paper>
    {/* {currentModel !== null &&
        <Paper>
            <FlexCol style={{
                gap: 5
            }}>
                <span><b>Model size: {b64encodedModel.length} bytes</b></span>
                <FlexRow>
                    <span><b>Model content:</b></span>
                    <JsonView
                        data={currentModel}
                        style={defaultStyles} shouldInitiallyExpand={(level) => {
                            return level < 3
                        }}
                    />
                    <JsonView
                        data={function(){
                            try {
                                return decodeModelBase64(b64encodedModel)
                            } catch(e: any) {
                                return {}
                            }
                        }()}
                        style={defaultStyles} shouldInitiallyExpand={(level) => {
                            return level < 3
                        }}
                    />
                </FlexRow>
                
            </FlexCol>
        </Paper>
    } */}
    </FlexCol>
}